// src/App.js
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';

import LoginPage       from './pages/LoginPage';
import AdminDashboard  from './pages/AdminDashboard';
import StaffDashboard  from './pages/StaffDashboard';
import AdminTasks      from './pages/AdminTasks';
import AdminUsers      from './pages/AdminUsers';
import AdminAnalytics  from './pages/AdminAnalytics';
import AdminShipments  from './pages/AdminShipments';
import AdminActivityLog from './pages/AdminActivityLog';
import AdminSettings   from './pages/AdminSettings';
import AdminRoles      from './pages/AdminRoles';
import StaffTasks      from './pages/StaffTasks';
import AllTasks        from './pages/AllTasks';
import StaffShipments  from './pages/StaffShipments';
import NotFound        from './pages/NotFound';

import './styles/global.css';

// ─── Route guards ─────────────────────────────────────────────
function PrivateRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  return user ? children : <Navigate to="/login" replace />;
}

function AdminRoute({ children }) {
  const { user, profile, loading } = useAuth();
  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  if (!user)                    return <Navigate to="/login"  replace />;
  if (profile?.role !== 'admin') return <Navigate to="/dashboard" replace />;
  return children;
}

// ─── App ──────────────────────────────────────────────────────
function AppRoutes() {
  const { user, profile, loading } = useAuth();

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  return (
    <Routes>
      <Route path="/login" element={
        user ? <Navigate to={profile?.role === 'admin' ? '/admin' : '/dashboard'} replace /> : <LoginPage />
      } />

      {/* Staff routes */}
      <Route path="/dashboard" element={<PrivateRoute><StaffDashboard /></PrivateRoute>} />
      <Route path="/my-tasks"   element={<PrivateRoute><StaffTasks /></PrivateRoute>} />
      <Route path="/all-tasks"  element={<PrivateRoute><AllTasks /></PrivateRoute>} />
      <Route path="/shipments" element={<PrivateRoute><StaffShipments /></PrivateRoute>} />

      {/* Admin routes */}
      <Route path="/admin"           element={<AdminRoute><AdminDashboard /></AdminRoute>} />
      <Route path="/admin/tasks"     element={<AdminRoute><AdminTasks /></AdminRoute>} />
      <Route path="/admin/users"     element={<AdminRoute><AdminUsers /></AdminRoute>} />
      <Route path="/admin/analytics" element={<AdminRoute><AdminAnalytics /></AdminRoute>} />
      <Route path="/admin/shipments" element={<AdminRoute><AdminShipments /></AdminRoute>} />
      <Route path="/admin/activity"  element={<AdminRoute><AdminActivityLog /></AdminRoute>} />
      <Route path="/admin/settings"  element={<AdminRoute><AdminSettings /></AdminRoute>} />
      <Route path="/admin/roles"     element={<AdminRoute><AdminRoles /></AdminRoute>} />

      {/* Default redirect */}
      <Route path="/" element={<Navigate to={user ? (profile?.role === 'admin' ? '/admin' : '/dashboard') : '/login'} replace />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}
